// @flow
import * as React from 'react';
import { withTranslation } from 'react-i18next';
import last from 'lodash/last';
import { withStyles } from '@material-ui/core/styles';
import { sumBy } from 'lodash';
import { getOS } from '../../utils';

const styles = theme => ({
  wrapper: {
    overflowY: 'auto',
    scrollBehavior: 'smooth',
    zIndex: 100,
  },
  scrollTarget: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  inCaptionMore: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  newTranscriptsButton: {
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 2,
    paddingBottom: 2,
    borderRadius: 4,
    position: 'fixed',
    bottom: 20,
    right: 20,
    backgroundColor: theme.palette.ava.blue2,
    border: 'none',
    '-webkit-app-region': 'no-drag',
    '&:hover': {
      backgroundColor: theme.palette.ava.blue1,
      cursor: 'pointer',
    },
    zIndex: 1,
  },
});

type Props = {
  bottomMenu: boolean,
  children: React.Node,
  electronCaptionMode: boolean,
  fullScreen: boolean,
  lightView: boolean,
  keyboardInputShown: boolean,
  sidebar: boolean,
  transcriptsCurrent: Array<string>,
  classes: Object,
  t: Function,
};

type State = {
  followingTranscript: boolean,
  isAtBottomOfTranscript: boolean,
  scrollHeightWhenStoppedFollowing: number,
  newTranscriptsWhenNotFollowing: boolean,
};

class ScribeScrollWithTranscript extends React.Component<Props, State> {
  scrollableContainer: ?HTMLElement;

  lastScrollTop = 0;
  lastScrollsUp = [];

  constructor(props: Props) {
    super(props);
    this.state = {
      followingTranscript: true,
      isAtBottomOfTranscript: true,
      scrollHeightWhenStoppedFollowing: 0,
      newTranscriptsWhenNotFollowing: false,
    };
    this.scrollableContainer = null;
  }

  componentDidUpdate(prevProps: Props) {
    const { keyboardInputShown, electronCaptionMode } = this.props;
    if (electronCaptionMode && !prevProps.electronCaptionMode) {
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({ followingTranscript: true });
    }
    this.updateIsAtBottomOfTranscript();
    this.updateNewTranscriptsWhenNotFollowing();
    if (this.scrollableContainer && keyboardInputShown !== prevProps.keyboardInputShown) {
      this.followIfShouldFollow();
      return;
    }
    const { transcriptsCurrent } = this.props;
    if (!last(transcriptsCurrent) || !last(prevProps.transcriptsCurrent)) return;
    if (transcriptsCurrent.length !== prevProps.transcriptsCurrent.length) {
      this.followIfShouldFollow();
      return;
    }
    this.followIfShouldFollow();
  }

  followIfShouldFollow() {
    const { followingTranscript } = this.state;
    if (!followingTranscript) {
      return;
    }
    // We don't auto scroll if there was a recent scroll up (i.e. < 1 second ago)
    const now = new Date().getTime();
    if (this.lastScrollsUp.length > 0 && now - this.lastScrollsUp[this.lastScrollsUp.length - 1].when < 1000) {
      return;
    }
    // setTimeout(..., 0) makes the browser execute the code directly after
    // this execution finishes. Without it the browser is not done rendering
    // the container and is unable to scroll it.
    setTimeout(() => {
      if (!this.scrollableContainer) return;
      this.scrollableContainer.scrollTop = this.scrollableContainer.scrollHeight;
    }, 0);
  }

  updateIsAtBottomOfTranscript() {
    if (!this.scrollableContainer) return;
    const newScrollTop = this.scrollableContainer.scrollTop;
    const isAtBottomOfTranscript =
      newScrollTop >= this.scrollableContainer.scrollHeight - this.scrollableContainer.clientHeight - 50;
    const oldIsAtBottomOfTranscript = this.state.isAtBottomOfTranscript;
    if (isAtBottomOfTranscript !== oldIsAtBottomOfTranscript) this.setState({ isAtBottomOfTranscript });
    return isAtBottomOfTranscript;
  }

  updateNewTranscriptsWhenNotFollowing() {
    const { newTranscriptsWhenNotFollowing, followingTranscript, scrollHeightWhenStoppedFollowing } = this.state;
    if (followingTranscript) return;
    const newTranscripts =
      scrollHeightWhenStoppedFollowing !== (this.scrollableContainer && this.scrollableContainer.scrollHeight);
    if (newTranscriptsWhenNotFollowing !== newTranscripts) {
      this.setState({ newTranscriptsWhenNotFollowing: newTranscripts });
    }
  }

  onScroll() {
    const { followingTranscript } = this.state;
    if (!this.scrollableContainer) return;
    const newScrollTop = this.scrollableContainer.scrollTop;
    const isAtBottomOfTranscript = this.updateIsAtBottomOfTranscript();
    if (newScrollTop < this.lastScrollTop) {
      const now = new Date().getTime();
      this.lastScrollsUp.push({
        when: now,
        scroll: this.lastScrollTop - newScrollTop,
      });
      // We keep all the scrolls of the last second
      this.lastScrollsUp = this.lastScrollsUp.filter(scroll => now - scroll.when < 1000);
      const amountOfScroll = sumBy(this.lastScrollsUp, 'scroll');
      if (amountOfScroll > 50 && followingTranscript) {
        this.setState({
          followingTranscript: false,
          scrollHeightWhenStoppedFollowing: this.scrollableContainer ? this.scrollableContainer.scrollHeight : 0,
          newTranscriptsWhenNotFollowing: false,
        });
      }
    } else if (isAtBottomOfTranscript && !followingTranscript) {
      // If we scroll to bottom, start following
      this.setState({ followingTranscript: true });
    }

    this.lastScrollTop = newScrollTop;
  }

  render() {
    const {
      bottomMenu,
      children,
      electronCaptionMode,
      fullScreen,
      lightView,
      keyboardInputShown,
      sidebar,
      classes,
      t,
    } = this.props;
    const { isAtBottomOfTranscript, followingTranscript, newTranscriptsWhenNotFollowing } = this.state;
    const headerHeight = fullScreen || lightView ? 0 : 99;
    return (
      <div
        className={`${classes.wrapper} ${electronCaptionMode ? classes.inCaptionMore : ''}`}
        style={{
          position: electronCaptionMode ? 'relative' : 'fixed',
          height: electronCaptionMode
            ? '100%'
            : `calc(100% - ${headerHeight}px - ${bottomMenu ? 4.5 : 0}rem - ${keyboardInputShown ? 70 : 0}px)`,
          width: electronCaptionMode ? '100%' : `calc(100% - ${sidebar ? 270 : 0}px)`,
          WebkitAppRegion: getOS() === 'Mac' && electronCaptionMode ? 'drag' : '',
          left: electronCaptionMode ? 0 : undefined,
          top: electronCaptionMode ? 0 : undefined,
          paddingRight: !electronCaptionMode ? '100px' : 'initial',
        }}
        ref={el => {
          if (el && this.scrollableContainer !== el) {
            this.scrollableContainer = el;
            this.scrollableContainer.addEventListener('scroll', () => {
              this.onScroll();
            });
          }
        }}
      >
        {!isAtBottomOfTranscript && !followingTranscript && newTranscriptsWhenNotFollowing && (
          <button
            className={classes.newTranscriptsButton}
            onClick={() => {
              this.lastScrollsUp = [];
              this.setState({ followingTranscript: true }, () => this.followIfShouldFollow());
            }}
          >
            {t('ccMode.conversation.newTranscripts')} &#8595;
          </button>
        )}
        <div className={`${classes.scrollTarget} ${electronCaptionMode ? classes.inCaptionMore : ''}`}>{children}</div>
      </div>
    );
  }
}

export default withTranslation()(withStyles(styles)(ScribeScrollWithTranscript));
